import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Paper, Typography, TextField, IconButton, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Select, MenuItem, InputAdornment, Tooltip, Stack, Chip, CircularProgress,
  Card, CardContent, Badge, Alert, Tabs, Tab, useTheme, useMediaQuery,
  Fade, Zoom
} from '@mui/material';
import {
  Search, Edit, Add, FileDownload, Warning, ArrowUpward, ArrowDownward,
  ShoppingCart, Inventory as InventoryIcon, Assessment, Schedule,
  TrendingUp, TrendingDown, LocalShipping, Restaurant, Visibility
} from '@mui/icons-material';
import { formatDistanceToNow, isAfter, addDays } from 'date-fns';
import { motion } from 'framer-motion';
import ingredientsService from '../inventory/services/ingredientsService.js';
import IngredientForm from './components/IngredientForm.jsx';
import IngredientStockAdjustmentDialog from './components/IngredientStockAdjustmentDialog.jsx';
import IngredientReceivingDialog from './components/IngredientReceivingDialog.jsx';
import IngredientDetailsDialog from './components/IngredientDetailsDialog.jsx';
import { STORAGE_LOCATIONS, calculateUnitCost } from './utils/ingredientUtils.js';

import apiClient from '../../../../api/client';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

// Smart number formatting - shows whole numbers without decimals, decimals when needed
const formatStockNumber = (value) => {
  const num = parseFloat(value || 0);
  if (num % 1 === 0) {
    // Whole number - show without decimals
    return Math.floor(num).toString();
  } else {
    // Has decimals - remove trailing zeros
    return num.toString().replace(/\.?0+$/, '');
  }
};

const Ingredients = ({ refreshSummary } = {}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [ingredients, setIngredients] = useState([]);
  const [recipeConsumption, setRecipeConsumption] = useState([]); // FIXED: Track recipe consumption from database
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [receivingDialogOpen, setReceivingDialogOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [selectedIngredientForView, setSelectedIngredientForView] = useState(null);

  const fetchIngredients = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await ingredientsService.getIngredients();
      const data = resp?.data?.data ?? resp?.data ?? [];
      setIngredients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading ingredients', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // FIXED: Fetch recipe consumption from database 
  const fetchRecipeConsumption = useCallback(async () => {
    try {
      // Use the correct API endpoint
      const response = await apiClient.get('/api/v1/inventory/recipe-ingredient-consumption');
      
      if (response?.data?.success) {
        setRecipeConsumption(response.data.data || []);
        console.log('✅ Loaded ingredient consumption data:', response.data.data.length, 'records');
      } else {
        console.warn('⚠️ Recipe consumption data not available');
        setRecipeConsumption([]);
      }
    } catch (err) {
      console.error('Error loading recipe consumption:', err);
      setRecipeConsumption([]);
    }
  }, []);

  // FIXED: Calculate total consumption per ingredient from database
  const getIngredientConsumption = useCallback((ingredientId) => {
    return recipeConsumption
      .filter(consumption => consumption.ingredient_id === ingredientId)
      .reduce((total, consumption) => total + parseFloat(consumption.quantity_per_yield || 0), 0);
  }, [recipeConsumption]);

  // FIXED: Calculate available stock (current_stock - recipe consumption)
  const calculateAvailableStock = useCallback((ingredient) => {
    const currentStockInDb = parseFloat(ingredient.current_stock || 0);
    const totalConsumption = getIngredientConsumption(ingredient.ingredient_id);
    const availableStock = Math.max(0, currentStockInDb - totalConsumption);
    
    return {
      originalStock: parseFloat(ingredient.package_size || 0),
      currentStockInDb: currentStockInDb,
      consumption: totalConsumption,
      availableStock: availableStock,
      consumptionPercentage: currentStockInDb > 0 ? (totalConsumption / currentStockInDb) * 100 : 0
    };
  }, [getIngredientConsumption]);

  // FIXED: Sync package_size with current_stock when ingredient is updated
  // const handleIngredientUpdate = useCallback(async (ingredientData) => {
  //   try {
  //     // When updating ingredient, sync current_stock with package_size if it's a new package
  //     const updatePayload = {
  //       ...ingredientData,
  //       // If package_size changed, reset current_stock to package_size
  //       current_stock: ingredientData.package_size
  //     };
      
  //     const response = await ingredientsService.updateIngredient(ingredientData.ingredient_id, updatePayload);
  //     return response;
  //   } catch (error) {
  //     console.error('Error updating ingredient with stock sync:', error);
  //     throw error;
  //   }
  // }, []);

  useEffect(() => {
    fetchIngredients();
    fetchRecipeConsumption();
  }, [fetchIngredients, fetchRecipeConsumption]);

  // Enhanced Summary Metrics with consumption tracking
  const summaryMetrics = useMemo(() => {
    const totalIngredients = ingredients.length;
    const activeIngredients = ingredients.filter(ing => ing.is_active).length;
    
    // Calculate low stock based on available stock (after recipe consumption)
    const lowStockIngredients = ingredients.filter(ing => {
      const stockInfo = calculateAvailableStock(ing);
      return stockInfo.availableStock <= parseFloat(ing.min_stock || 0) && stockInfo.availableStock > 0;
    });
    
    const outOfStockIngredients = ingredients.filter(ing => {
      const stockInfo = calculateAvailableStock(ing);
      return stockInfo.availableStock === 0;
    });
    
    const totalValue = ingredients.reduce((sum, ing) => {
      const unitCost = calculateUnitCost(ing.package_cost, ing.package_size);
      const stockInfo = calculateAvailableStock(ing);
      const stockValue = stockInfo.availableStock * unitCost;
      return sum + stockValue;
    }, 0);
    
    const expiringIngredients = ingredients.filter(ing => 
      ing.expiry_date_on_purchase && 
      isAfter(addDays(new Date(), 7), new Date(ing.expiry_date_on_purchase))
    );

    const avgCostPerUnit = ingredients.length > 0 ? 
      ingredients.reduce((sum, ing) => sum + calculateUnitCost(ing.package_cost, ing.package_size), 0) / ingredients.length : 0;

    // Calculate total recipe consumption across all ingredients
    const totalRecipeConsumption = ingredients.reduce((sum, ing) => {
      return sum + getIngredientConsumption(ing.ingredient_id);
    }, 0);

    return { 
      totalIngredients, 
      activeIngredients,
      lowStockCount: lowStockIngredients.length, 
      outOfStockCount: outOfStockIngredients.length,
      totalValue, 
      expiringCount: expiringIngredients.length,
      avgCostPerUnit,
      lowStockIngredients,
      outOfStockIngredients,
      expiringIngredients,
      totalRecipeConsumption
    };
  }, [ingredients, calculateAvailableStock, getIngredientConsumption]);

  const filtered = useMemo(() => {
    let baseFilter = ingredients;
    
    // Tab filtering
    switch (selectedTab) {
      case 1: // Low Stock
        baseFilter = summaryMetrics.lowStockIngredients;
        break;
      case 2: // Out of Stock  
        baseFilter = summaryMetrics.outOfStockIngredients;
        break;
      case 3: // Expiring
        baseFilter = summaryMetrics.expiringIngredients;
        break;
      default: // All
        baseFilter = ingredients;
    }
    
    return baseFilter
      .filter(i => filterType === 'all' ? true : (i.type === filterType))
      .filter(i => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return `${i.name} ${i.supplier_name || ''} ${i.storage_location || ''}`.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const vA = (a[sort.key] ?? '').toString();
        const vB = (b[sort.key] ?? '').toString();
        return sort.dir === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
      });
  }, [ingredients, selectedTab, summaryMetrics, filterType, search, sort]);

  // Handle View Details
  const handleViewDetails = (ingredient) => {
    setSelectedIngredientForView(ingredient);
    setViewDetailsDialogOpen(true);
  };

  const handleReceiveStock = (ingredient) => {
    setSelectedIngredient(ingredient);
    setReceivingDialogOpen(true);
  };

  const handleAdjustStock = (ingredient) => {
    setSelectedIngredient(ingredient);
    setAdjustDialogOpen(true);
  };

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setOpenForm(true);
  };

  // FIXED: Enhanced dialog close handlers that refresh both ingredients and consumption data
  const handleDialogClose = useCallback(() => {
    fetchIngredients();
    fetchRecipeConsumption(); // Refresh consumption data
    if (typeof refreshSummary === 'function') refreshSummary();
  }, [fetchIngredients, fetchRecipeConsumption, refreshSummary]);

  const exportCsv = () => {
    if (!filtered.length) {
      alert(t('ingredients.messages.noData') || 'No data to export');
      return;
    }

    const headers = [
      'Name', 'Type', 'Package Size', 'Package UOM', 'Package Cost', 'Unit Cost', 
      'Current Stock (DB)', 'Available Stock', 'Recipe Consumption', 'Consumption %', 'Min Stock', 'Max Stock', 'Storage Location',
      'Supplier', 'Purchase Date', 'Expiry Date', 'Status'
    ];

    const csv = [
      headers.join(','),
      ...filtered.map(r => {
        const unitCost = calculateUnitCost(r.package_cost, r.package_size);
        const stockInfo = calculateAvailableStock(r);
        const status = stockInfo.availableStock === 0 ? 'Out of Stock' : 
                     stockInfo.availableStock <= parseFloat(r.min_stock || 0) ? 'Low Stock' : 'Normal';
        
        return [
          `"${r.name || ''}"`,
          `"${r.type || 'raw'}"`,
          formatStockNumber(r.package_size),
          `"${r.package_uom || r.unit_of_measure || ''}"`,
          parseFloat(r.package_cost || 0).toFixed(2),
          unitCost.toFixed(6),
          formatStockNumber(r.current_stock),
          formatStockNumber(stockInfo.availableStock),
          formatStockNumber(stockInfo.consumption),
          stockInfo.consumptionPercentage.toFixed(1),
          formatStockNumber(r.min_stock),
          formatStockNumber(r.max_stock),
          `"${r.storage_location || ''}"`,
          `"${r.supplier_name || ''}"`,
          `"${r.purchase_date || ''}"`,
          `"${r.expiry_date_on_purchase || ''}"`,
          `"${status}"`
        ].join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ingredients-with-consumption-${Date.now()}.csv`;
    a.click();
  };

  const getExpiryStatus = (ingredient) => {
    const today = new Date();
    const purchaseExpiry = ingredient.expiry_date_on_purchase ? new Date(ingredient.expiry_date_on_purchase) : null;
    const openedExpiry = ingredient.expiry_date_after_opened ? new Date(ingredient.expiry_date_after_opened) : null;
    
    const isExpired = (date) => date && isAfter(today, date);
    const isExpiringSoon = (date) => date && isAfter(addDays(today, 7), date) && !isExpired(date);

    if (isExpired(purchaseExpiry) || isExpired(openedExpiry)) {
      return { status: 'expired', color: 'error', text: 'Expired' };
    }
    if (isExpiringSoon(purchaseExpiry) || isExpiringSoon(openedExpiry)) {
      return { status: 'expiring', color: 'warning', text: 'Expiring Soon' };
    }
    return { status: 'good', color: 'success', text: 'Good' };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Beautiful Header */}
      <MotionPaper
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Restaurant sx={{ fontSize: 40, color: 'white' }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                {t('ingredients.title') || 'Ingredients Management'}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {t('ingredients.subtitle') || 'Manage your ingredient inventory with precision and control'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </MotionPaper>

      {/* Enhanced Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MotionCard
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
              border: `2px solid ${theme.palette.primary.main}20`,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <InventoryIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                {summaryMetrics.totalIngredients}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('ingredients.totalIngredients') || 'Total Ingredients'}
              </Typography>
              <Chip 
                label={`${summaryMetrics.activeIngredients} Active`}
                size="small" 
                sx={{ bgcolor: theme.palette.success.main, color: 'white', fontWeight: 600 }}
              />
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MotionCard
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.main}05 100%)`,
              border: `2px solid ${theme.palette.warning.main}20`,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Badge badgeContent={summaryMetrics.outOfStockCount} color="error">
                <Warning sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
              </Badge>
              <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.warning.main, mb: 1 }}>
                {summaryMetrics.lowStockCount}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('ingredients.lowStockAlerts') || 'Low Stock Items'}
              </Typography>
              {summaryMetrics.outOfStockCount > 0 && (
                <Chip 
                  label={`${summaryMetrics.outOfStockCount} Out of Stock`}
                  size="small" 
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MotionCard
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.main}05 100%)`,
              border: `2px solid ${theme.palette.success.main}20`,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                ${summaryMetrics.totalValue.toFixed(0)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('ingredients.totalInventoryValue') || 'Available Inventory Value'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg: ${summaryMetrics.avgCostPerUnit.toFixed(2)}/unit
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MotionCard
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.main}05 100%)`,
              border: `2px solid ${theme.palette.info.main}20`,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 48, color: theme.palette.info.main, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.info.main, mb: 1 }}>
                {summaryMetrics.totalRecipeConsumption.toFixed(0)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Recipe Consumption
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total units consumed
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* Enhanced Filter Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 64
            }
          }}
        >
          <Tab 
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <InventoryIcon fontSize="small" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>All Ingredients</Typography>
                  <Typography variant="caption" color="text.secondary">{summaryMetrics.totalIngredients} items</Typography>
                </Box>
              </Stack>
            }
          />
          <Tab 
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Warning fontSize="small" color="warning" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Low Stock</Typography>
                  <Typography variant="caption" color="text.secondary">{summaryMetrics.lowStockCount} alerts</Typography>
                </Box>
              </Stack>
            }
          />
          <Tab 
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Assessment fontSize="small" color="error" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Out of Stock</Typography>
                  <Typography variant="caption" color="text.secondary">{summaryMetrics.outOfStockCount} items</Typography>
                </Box>
              </Stack>
            }
          />
          <Tab 
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Schedule fontSize="small" color="info" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Expiring Soon</Typography>
                  <Typography variant="caption" color="text.secondary">{summaryMetrics.expiringCount} items</Typography>
                </Box>
              </Stack>
            }
          />
        </Tabs>

        {/* Filters and Actions */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <Select 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)} 
                  size="small"
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="all">{t('ingredients.filters.all') || 'All Types'}</MenuItem>
                  <MenuItem value="raw">{t('ingredients.filters.allIngredients') || 'Raw Ingredients'}</MenuItem>
                  <MenuItem value="premade">{t('ingredients.filters.premade') || 'Pre-made Items'}</MenuItem>
                </Select>
                <TextField
                  size="small"
                  placeholder={t('ingredients.searchPlaceholder') || 'Search ingredients...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  sx={{ flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>)
                  }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button 
                  startIcon={<FileDownload />} 
                  onClick={exportCsv} 
                  variant="outlined" 
                  size="small"
                >
                  {t('ingredients.export.title') || 'Export'}
                </Button>
                <Button 
                  startIcon={<Add />} 
                  onClick={() => { setEditingIngredient(null); setOpenForm(true); }} 
                  variant="contained" 
                  size="small"
                >
                  {t('ingredients.add') || 'Add Ingredient'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Enhanced Table */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        variant="outlined" 
        sx={{ borderRadius: 2, overflow: 'hidden' }}
      >
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => setSort(prev => ({ key: 'name', dir: prev.key === 'name' && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('ingredients.name') || 'Ingredient'}
                    {sort.key === 'name' && (sort.dir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('ingredients.type') || 'Type'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('ingredients.packageInfo') || 'Package Info'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('ingredients.unitCost') || 'Unit Cost'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>DB Stock</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Available Stock</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Recipe Usage</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('ingredients.storage') || 'Storage'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('ingredients.expiry') || 'Expiry Status'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>{t('ingredients.actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedTab === 0 ? (t('ingredients.noIngredients') || 'No ingredients found') :
                         selectedTab === 1 ? 'No low stock alerts' :
                         selectedTab === 2 ? 'No out of stock items' : 'No expiring items'}
                      </Typography>
                      {selectedTab === 0 && (
                        <Button 
                          variant="contained" 
                          startIcon={<Add />}
                          onClick={() => { setEditingIngredient(null); setOpenForm(true); }}
                          sx={{ mt: 2 }}
                        >
                          Add Your First Ingredient
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filtered.map(row => {
                const stockInfo = calculateAvailableStock(row);
                const low = stockInfo.availableStock <= parseFloat(row.min_stock || 0);
                const outOfStock = stockInfo.availableStock === 0;
                const unitCost = calculateUnitCost(row.package_cost, row.package_size);
                const expiryStatus = getExpiryStatus(row);
                const unitLabel = row.package_uom || row.unit_of_measure || 'pcs';
                
                const hasHighConsumption = stockInfo.consumptionPercentage > 50;
                const consumptionColor = stockInfo.consumptionPercentage > 80 ? 'error' : 
                                       stockInfo.consumptionPercentage > 50 ? 'warning' : 'success';

                return (
                  <TableRow 
                    key={row.ingredient_id} 
                    sx={{ 
                      background: outOfStock ? 'rgba(244, 67, 54, 0.05)' : 
                                 low ? 'rgba(255, 152, 0, 0.05)' : 
                                 hasHighConsumption ? 'rgba(255, 193, 7, 0.03)' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderLeft: outOfStock ? '4px solid #f44336' : 
                                 low ? '4px solid #ff9800' :
                                 hasHighConsumption ? '4px solid #ffc107' : 'none'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          {outOfStock && (
                            <Chip label="Out of Stock" color="error" size="small" />
                          )}
                          {!outOfStock && low && (
                            <Chip label="Low Stock" color="warning" size="small" />
                          )}
                          {hasHighConsumption && (
                            <Chip label="High Usage" color={consumptionColor} size="small" />
                          )}
                          {!row.is_active && (
                            <Chip label="Inactive" color="default" size="small" />
                          )}
                        </Stack>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip 
                        label={row.type === 'premade' ? (t('ingredients.premade') || 'Pre-made') : (t('ingredients.raw') || 'Raw')}
                        color={row.type === 'premade' ? 'secondary' : 'primary'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatStockNumber(row.package_size)} {unitLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ${parseFloat(row.package_cost || 0).toFixed(2)} per package
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          ${unitCost.toFixed(4)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          per {unitLabel}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* FIXED: Show current stock from database */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {formatStockNumber(stockInfo.currentStockInDb)} {unitLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Database stock
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* FIXED: Show available stock (after recipe consumption) */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: outOfStock ? 'error.main' : 
                                   low ? 'warning.main' : 'success.main'
                          }}
                        >
                          {formatStockNumber(stockInfo.availableStock)} {unitLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Min: {formatStockNumber(row.min_stock)} | Max: {formatStockNumber(row.max_stock)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* FIXED: Show recipe consumption */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: consumptionColor + '.main' }}>
                          {formatStockNumber(stockInfo.consumption)} {unitLabel}
                        </Typography>
                        <Chip 
                          label={`${stockInfo.consumptionPercentage.toFixed(0)}%`}
                          size="small" 
                          color={consumptionColor}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          {row.storage_location || '-'}
                        </Typography>
                        {row.supplier_name && (
                          <Typography variant="caption" color="text.secondary">
                            {row.supplier_name}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip 
                          label={expiryStatus.text} 
                          color={expiryStatus.color} 
                          size="small" 
                        />
                        {row.expiry_date_on_purchase && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.expiry_date_on_purchase).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatDistanceToNow(new Date(row.expiry_date_on_purchase), { addSuffix: true })}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell align="center" sx={{ minWidth: 120 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Tooltip title={t('ingredients.edit') || 'Edit'}>
                          <IconButton size="small" onClick={() => handleEditIngredient(row)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Receive Stock">
                          <IconButton size="small" onClick={() => handleReceiveStock(row)} color="success">
                            <LocalShipping fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('ingredients.adjustStock') || 'Adjust Stock'}>
                          <IconButton size="small" onClick={() => handleAdjustStock(row)} color="warning">
                            <Assessment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleViewDetails(row)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </MotionPaper>

      {/* Dialogs */}
      <IngredientForm 
        open={openForm} 
        initialData={editingIngredient}
        onClose={() => { 
          setOpenForm(false); 
          setEditingIngredient(null);
          handleDialogClose();
        }} 
      />
      
      <IngredientStockAdjustmentDialog
        open={adjustDialogOpen}
        ingredient={selectedIngredient}
        onClose={() => { 
          setAdjustDialogOpen(false); 
          setSelectedIngredient(null);
          handleDialogClose();
        }}
      />

      <IngredientReceivingDialog
        open={receivingDialogOpen}
        ingredient={selectedIngredient}
        onClose={() => { 
          setReceivingDialogOpen(false); 
          setSelectedIngredient(null);
          handleDialogClose();
        }}
      />

      <IngredientDetailsDialog
        open={viewDetailsDialogOpen}
        ingredient={selectedIngredientForView}
        onClose={() => { 
          setViewDetailsDialogOpen(false); 
          setSelectedIngredientForView(null);
        }}
      />
    </Box>
  );
};

export default Ingredients;